import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { selectionProcessesAdmin } from '@/services/selectionProcesses';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PATHS } from '@/routes/paths';
import OverviewTab from './OverviewTab';
import RegistrationsTab from './RegistrationsTab';
import PublicLinkCard from './PublicLinkCard';
import { DashboardContent } from '@/layouts/dashboard';
import { Loader } from '@/components/custom/loader';
import { CardMetric, CardMetricWrapper, CardMetricHeader, CardMetricTitle, CardMetricBody, CardMetricValue } from '@/components/ui/card-metric';

const STATUS_CONFIG = {
  draft: { label: 'Rascunho', variant: 'warning' },
  open: { label: 'Aberto', variant: 'success' },
  closed: { label: 'Encerrado', variant: 'neutral-lightest' }
};

const AUDIENCE_LABEL = { student: 'Aluno', teacher: 'Professor' };

export default function SelectionProcessDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [process, setProcess] = useState(null);
  const [summary, setSummary] = useState(null);

  const load = () => selectionProcessesAdmin.get(id).then(res => setProcess(res.data));

  useEffect(() => {
    load();
  }, [id]);

  // Fetch summary (with polling when open) — lifted from OverviewTab so the KPI
  // in the top grid also stays live.
  useEffect(() => {
    if (!process) return;
    const currentStatus = process.attributes.current_status ?? process.attributes.status;
    let cancel = false;
    const loadSummary = () => selectionProcessesAdmin.summary(id).then(s => !cancel && setSummary(s));
    loadSummary();
    if (currentStatus === 'open') {
      const t = setInterval(loadSummary, 30000);
      return () => {
        cancel = true;
        clearInterval(t);
      };
    }
    return () => {
      cancel = true;
    };
  }, [id, process?.attributes?.current_status, process?.attributes?.status]);

  const breadcrumbs = {
    title: 'Inscrições',
    nav: [
      {
        route: PATHS.ADMIN_SELETIVO.LIST,
        name: 'Seletivos',
        isActive: false
      },
      {
        name: process?.attributes?.name ?? '…',
        isActive: true
      }
    ]
  };

  if (!process) {
    return (
      <DashboardContent
        variant="plain"
        breadcrumbs={breadcrumbs}
      >
        <Loader />
      </DashboardContent>
    );
  }

  const a = process.attributes;
  const statusCfg = STATUS_CONFIG[a.current_status ?? a.status] ?? STATUS_CONFIG.closed;

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      {/* ── Page header ── */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h2 className="mb-0 text-xl font-bold text-foreground">{a.name}</h2>
          <Badge
            variant={statusCfg.variant}
            size="default"
          >
            {statusCfg.label}
          </Badge>
          <Badge
            variant="neutral-lightest"
            size="default"
          >
            {AUDIENCE_LABEL[a.target_audience] || 'Aluno'}
          </Badge>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(PATHS.ADMIN_SELETIVO.EDIT(id))}
          >
            Editar
          </Button>
          {a.status === 'draft' && (
            <Button
              size="sm"
              onClick={() => selectionProcessesAdmin.publish(id).then(load)}
            >
              Publicar
            </Button>
          )}
          {a.status === 'open' && a.current_status === 'open' && (
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => selectionProcessesAdmin.close(id).then(load)}
            >
              Encerrar
            </Button>
          )}
        </div>
      </div>

      {/* ── Top grid: public link + total KPI ── */}
      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        <PublicLinkCard url={`${window.location.origin}/inscricao/${a.slug}`} />

        <CardMetricWrapper className="h-full">
          <CardMetric
            variant="light"
            className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center"
          >
            <CardMetricHeader className="m-0">
              <CardMetricTitle className="m-0 text-sm font-medium uppercase tracking-wide text-slate-500">Total de inscritos</CardMetricTitle>
            </CardMetricHeader>
            <CardMetricBody className="m-0">
              <CardMetricValue className="text-5xl font-bold tabular-nums leading-none text-slate-900">{summary ? summary.total : '—'}</CardMetricValue>
            </CardMetricBody>
          </CardMetric>
        </CardMetricWrapper>
      </div>

      {/* ── Tabs ── */}
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Resumo</TabsTrigger>
          <TabsTrigger value="registrations">Inscritos</TabsTrigger>
        </TabsList>
        <TabsContent value="overview">
          <OverviewTab summary={summary} />
        </TabsContent>
        <TabsContent value="registrations">
          <RegistrationsTab
            processId={id}
            slug={a.slug}
          />
        </TabsContent>
      </Tabs>
    </DashboardContent>
  );
}
